/**
 * Cambio de contraseña OBLIGATORIO (contrato F1) — pantalla BLOQUEANTE.
 *
 * La usan los DOS portales del marketplace (cliente y partner) desde su
 * ProtectedRoute: mientras el backend diga `must_change_password`, aquí no se
 * pinta ni el layout ni la ruta pedida — solo esto. Es el mismo patrón (y el
 * mismo texto) que el gate del empleado en el skill wai-agent-app-builder
 * (assets/frontend/src/auth/ChangePasswordGate.tsx).
 *
 * Recibe TODO por props (email, onSubmit, onCancel) a propósito: así no importa
 * ningún contexto de auth y sirve igual a los dos portales, que tienen contextos
 * distintos y hasta temas distintos.
 */
import React, { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';

/** Mismo mínimo que valida el backend (`portal_auth_policy.MIN_PASSWORD_LENGTH`). */
export const MIN_PASSWORD_LENGTH = 8;

interface Props {
  email?: string | null;
  /** Debe lanzar si el backend rechaza; el error se pinta en el Alert. */
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>;
  onCancel: () => void;
}

const ChangePasswordGate: React.FC<Props> = ({ email, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = repeat.length > 0 && next !== repeat;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next.length < MIN_PASSWORD_LENGTH) {
      setError(
        t('portalAuth.changePassword.tooShort', {
          count: MIN_PASSWORD_LENGTH,
          defaultValue: `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`,
        })
      );
      return;
    }
    if (next !== repeat) {
      setError(t('portalAuth.changePassword.mismatch', 'Las contraseñas no coinciden.'));
      return;
    }
    setLoading(true);
    try {
      await onSubmit(current, next);
    } catch (err: any) {
      // Los clientes HTTP de los dos portales normalizan el error a `{detail}`;
      // el backend ya manda ahí un mensaje concreto y en español.
      setError(
        err?.detail ||
          err?.message ||
          t('portalAuth.changePassword.error', 'No se ha podido cambiar la contraseña. Inténtalo de nuevo.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 420 }} elevation={0} variant="outlined">
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 800 }}>
            {t('portalAuth.changePassword.title', 'Crea tu contraseña')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t(
              'portalAuth.changePassword.subtitle',
              'Has entrado con una contraseña temporal de un solo uso. Crea tu contraseña definitiva para continuar.'
            )}
            {email ? ` (${email})` : ''}
          </Typography>

          <Box component="form" onSubmit={submit}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              {/* La temporal se pide SIEMPRE: ni el portal cliente ni el partner
                  la guardan en memoria tras el login, y una recarga de página
                  dejaría el formulario sin ella. */}
              <TextField
                label={t('portalAuth.changePassword.currentPassword', 'Contraseña temporal')}
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                fullWidth
                autoFocus
                autoComplete="current-password"
              />
              <TextField
                label={t('portalAuth.changePassword.newPassword', 'Nueva contraseña')}
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                error={tooShort}
                helperText={
                  tooShort
                    ? t('portalAuth.changePassword.tooShort', {
                        count: MIN_PASSWORD_LENGTH,
                        defaultValue: `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`,
                      })
                    : t('portalAuth.changePassword.hint', {
                        count: MIN_PASSWORD_LENGTH,
                        defaultValue: `Mínimo ${MIN_PASSWORD_LENGTH} caracteres.`,
                      })
                }
              />
              <TextField
                label={t('portalAuth.changePassword.confirmPassword', 'Repite la nueva contraseña')}
                type="password"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                error={mismatch}
                helperText={mismatch ? t('portalAuth.changePassword.mismatch', 'Las contraseñas no coinciden.') : ' '}
              />
              <Button type="submit" variant="contained" size="large" disabled={loading}>
                {loading
                  ? t('portalAuth.changePassword.saving', 'Guardando…')
                  : t('portalAuth.changePassword.submit', 'Guardar y entrar')}
              </Button>
              <Button type="button" variant="text" size="small" onClick={onCancel} disabled={loading}>
                {t('portalAuth.changePassword.signOut', 'Cerrar sesión')}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChangePasswordGate;
